import { useContext, useEffect, useState, FC, useMemo } from 'react'
import { Grid, Typography, NothingHereIcon } from '@hermes/web-components'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { SearchTypes } from '../../../../providers/SearchProfilesPage/types'
import useGetProfilesToShow from '../../../../hooks/useGetProfilesToShow'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import SearchResultEmptyStateCard from '../../../search/SearchResultEmptyStateCard'
import { IntroductionVideo, PracticeProfile, SpecialistProfile } from '../../../../types'
import DefaultSearchProfileCard from '../../Default/SearchResults/SearchProfileCard'
import AlternativeSearchProfileCard from '../../Alternative/SearchResults/SearchProfileCard'
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags'
import { TENANTS } from '../../../../constants/global'
import { AppDataContext } from '../../../../providers/AppData'
import SuggestedSearch from '../../Alternative/SuggestedSearch'
import { SearchCardsDivider } from './styles'

const VideoPlayerModal = dynamic(() => import('../../../profiles/VideoPlayerModal'), { ssr: false })

const VIDEO_PLAYER_MODAL_DEFAULT_DATA = {
  mediaId: 0,
  specialistId: 0,
  practiceId: 0,
  url: ''
}

type SearchData = PracticeProfile | SpecialistProfile

type SearchCardsData = (SearchData & {
  isSponsor?: boolean
  isSuggested?: boolean
  alternativeSearchCard?: boolean
})[]

type SearchDisplayCardsProps = {
  sponsors?: SearchData[]
  locale: string
  shouldShowSponsors: boolean
  currentSearchSpecialist: boolean
  setVideoPlayerModalData: (data: IntroductionVideo) => void
  profilesToShow: SearchData[]
  suggestedProfiles?: SearchData[]
  alternativeSearchEnabled: boolean
  shouldShowSuggested?: boolean
  translatedName: string
}

const SearchDisplayCards: FC<SearchDisplayCardsProps> = ({
  sponsors,
  locale,
  shouldShowSponsors,
  currentSearchSpecialist,
  setVideoPlayerModalData,
  translatedName,
  profilesToShow,
  shouldShowSuggested,
  suggestedProfiles,
  alternativeSearchEnabled
}) => {
  const searchCardsData = useMemo(() => {
    const mapSponsors = (items?: SearchData[] | null) =>
      items?.map((profile) => ({ ...profile, isSponsor: true })) ?? []

    const buildSearchData = () => {
      if (sponsors?.length === 2) {
        const [firstSponsor, secondSponsor] = sponsors

        return [
          ...(shouldShowSponsors && firstSponsor ? [{ ...firstSponsor, isSponsor: true }] : []),
          ...(profilesToShow?.slice(0, 3) ?? []),
          ...(shouldShowSponsors && secondSponsor ? [{ ...secondSponsor, isSponsor: true }] : []),
          ...(profilesToShow?.slice(3) ?? [])
        ]
      }

      return [
        ...(shouldShowSponsors ? mapSponsors(sponsors) : []),
        ...(profilesToShow?.slice(0, 3) ?? []),
        ...(shouldShowSuggested && suggestedProfiles?.length ? [{ ...suggestedProfiles, isSuggested: true }] : []),
        ...(profilesToShow?.slice(3) ?? [])
      ]
    }

    return buildSearchData().map((profile) => ({
      ...profile,
      alternativeSearchCard: alternativeSearchEnabled && locale !== TENANTS.WPA
    })) as SearchCardsData
  }, [
    alternativeSearchEnabled,
    locale,
    profilesToShow,
    shouldShowSponsors,
    shouldShowSuggested,
    sponsors,
    suggestedProfiles
  ])

  return searchCardsData.map((profile, index) => {
    const { alternativeSearchCard, isSuggested, isSponsor } = profile

    return alternativeSearchCard ? (
      <>
        {index === 0 && !isSponsor && <SearchCardsDivider sx={{ height: '1px' }} />}
        {index !== 0 && index !== searchCardsData.length && <SearchCardsDivider />}
        {isSuggested && (
          <SuggestedSearch
            key={`suggested-search-${index}`}
            profilesToShow={suggestedProfiles}
            currentSearchSpecialist={currentSearchSpecialist}
            handleSetVideoPlayerModalData={setVideoPlayerModalData}
            translatedName={translatedName}
          />
        )}
        {!isSuggested && (
          <AlternativeSearchProfileCard
            key={`profile-card-${profile.id}`}
            {...(currentSearchSpecialist
              ? { specialist: profile as SpecialistProfile }
              : { practice: profile as PracticeProfile })}
            isSponsor={isSponsor}
            handleSetVideoPlayerModalData={setVideoPlayerModalData}
          />
        )}
      </>
    ) : (
      <DefaultSearchProfileCard
        key={`profile-card-${profile.id}`}
        {...(currentSearchSpecialist
          ? { specialist: profile as SpecialistProfile }
          : { practice: profile as PracticeProfile })}
        handleSetVideoPlayerModalData={setVideoPlayerModalData}
      />
    )
  })
}

function SearchProfilesResults() {
  const translate = useTranslateMessage()
  const router = useRouter()
  const { alternativeSearchEnabled } = useFeatureFlags()
  const { locale } = useContext(AppDataContext)

  const [videoPlayerModalData, setVideoPlayerModalData] = useState<IntroductionVideo>(VIDEO_PLAYER_MODAL_DEFAULT_DATA)
  const {
    state: {
      filters,
      specialists,
      specialistsSponsors,
      practiceSponsors,
      practices,
      isLoading,
      isEmptyResponse,
      currentLocation,
      clientReloaded,
      suggestedSpecialists,
      suggestedPractices,
      shouldShowSuggested
    }
  } = useContext(SearchProfilesPageContext)

  const translatedName = useMemo(() => {
    switch (filters.searchType) {
      case SearchTypes.healthAndSocialCare:
        return translate('home-care-specs')
      case SearchTypes.hospital:
        return translate('hospitals')
      case SearchTypes.pharmacy:
        return translate('pharmacies')
      case SearchTypes.specialists:
        return translate('specialists')
      case SearchTypes.careHome:
        return translate('care-home')
      default:
        return translate('practices')
    }
  }, [filters.searchType, translate])

  const [, setRadiusExpanding] = useState(false)

  const currentSearchSpecialist =
    filters.searchType === SearchTypes.specialists || filters.searchType === SearchTypes.healthAndSocialCare

  const profilesToShow = useGetProfilesToShow()

  const handleCloseVideoModal = () => setVideoPlayerModalData(VIDEO_PLAYER_MODAL_DEFAULT_DATA)

  useEffect(() => {
    setRadiusExpanding(
      currentLocation &&
        !router.asPath.includes('page-') &&
        !window.location.href.includes('distance=') &&
        (filters.searchType === SearchTypes.specialists || filters.searchType === SearchTypes.healthAndSocialCare
          ? !specialists.length
          : !practices.length) &&
        filters.distance !== '150'
    )
    // todo: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.distance, filters.searchType, practices.length, specialists.length, currentLocation])

  if (isLoading) {
    return (
      <Grid container xs={12} display="flex" justifyContent="center" alignItems="center" gap="16px">
        <SearchResultEmptyStateCard />
        <SearchResultEmptyStateCard />
        <SearchResultEmptyStateCard />
      </Grid>
    )
  }

  const sponsors = practiceSponsors?.length ? practiceSponsors : specialistsSponsors

  const suggestedProfiles = currentSearchSpecialist ? suggestedSpecialists : suggestedPractices

  return (
    <>
      {isEmptyResponse || !profilesToShow.length ? (
        <Grid container sx={{ width: '100%', py: 12 }} alignItems="center" direction="column">
          <NothingHereIcon />
          <Typography textAlign="center" variant="h2">
            {translate('nothing_here')}
          </Typography>
        </Grid>
      ) : null}
      <SearchDisplayCards
        sponsors={sponsors}
        suggestedProfiles={suggestedProfiles}
        shouldShowSuggested={shouldShowSuggested}
        locale={locale}
        shouldShowSponsors={!clientReloaded}
        translatedName={translatedName}
        currentSearchSpecialist={currentSearchSpecialist}
        setVideoPlayerModalData={setVideoPlayerModalData}
        profilesToShow={profilesToShow}
        alternativeSearchEnabled={alternativeSearchEnabled}
      />
      {!!videoPlayerModalData?.url?.length && (
        <VideoPlayerModal
          open={true}
          url={videoPlayerModalData.url}
          mediaId={videoPlayerModalData.mediaId}
          practiceId={videoPlayerModalData.practiceId}
          specialistId={videoPlayerModalData.practiceId}
          onClose={handleCloseVideoModal}
        />
      )}
    </>
  )
}

export default SearchProfilesResults
